import { HttpClient } from '@adapters/HttpClient';
import {
  CreateLocationRequest,
  Location,
  LocationFilters,
  LocationPage,
  LocationsApiResponse,
  Product,
} from '../models/Location';
import { UpdateLocationRequest } from '../services/LocationService';

export class LocationApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // 🎯 Método original para compatibilidad hacia atrás
  async getLocations(filters: LocationFilters = {}): Promise<Location[]> {
    const response = await this.getLocationsPaginated(filters);
    return response.locations;
  }

  // 🎯 Nuevo método para scroll infinito - retorna respuesta completa
  async getLocationsPaginated(filters: LocationFilters = {}): Promise<LocationPage> {
    const {
      searchTerm = '',
      productId,
      typeId,
      type_ids, // 🎯 Campos ya procesados desde filterConditions
      product_ids, // 🎯 Campos ya procesados desde filterConditions
      product_team_ids,
      sortBy, // 🎯 Campo para ordenamiento
      sortDirection, // 🎯 Dirección del ordenamiento
      page = 1,
      limit = 20,
    } = filters;

    const params: any = {
      ...(searchTerm && { search: searchTerm }),
      ...(productId && { product_ids: productId }),
      ...(typeId && { type_ids: typeId }),
      // 🎯 Usar campos específicos directamente (pueden sobrescribir los anteriores)
      ...(type_ids && { type_ids }),
      ...(product_ids && { product_ids }),
      ...(product_team_ids && { product_team_ids }),
      // 🎯 Parámetros de sorting
      ...(sortBy && { order_by: sortBy }),
      ...(sortDirection && { order_direction: sortDirection }),
      page,
      per_page: limit,
    };

    // 🎯 Lógica simple: mostrar barra global solo en página 1
    const showGlobalLoading = page === 1;

    const response = await this.httpClient.get<LocationsApiResponse>(
      '/locations',
      params,
      {}, // config
      showGlobalLoading,
    );

    // 🎯 Transformar respuesta API a formato para useInfiniteQuery
    return {
      locations: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total, // 🎯 Total de resultados del API
    };
  }

  // 🎯 Obtener productos para filtros
  async getProducts(): Promise<Product[]> {
    const response = await this.httpClient.get<{ data: Product[] }>('/products', {
      fields: 'basic',
      per_page: 10000,
    });

    return response.data || [];
  }

  // 🎯 Crear nueva ubicación
  async createLocation(data: CreateLocationRequest): Promise<Location> {
    const response = await this.httpClient.post<Location>('/locations', data);
    return response;
  }

  // 🎯 Actualizar ubicación existente
  async updateLocation(id: number, data: UpdateLocationRequest): Promise<Location> {
    const response = await this.httpClient.put<Location>(`/locations/${id}`, data);
    return response;
  }

  // 🎯 Eliminar ubicación
  async deleteLocation(id: number): Promise<void> {
    await this.httpClient.delete(`/locations/${id}`);
  }

  // 🎯 Bloquear ubicación
  async lockLocation(id: number): Promise<Location> {
    const response = await this.httpClient.put<Location>(`/locations/${id}/lock`, {});
    return response;
  }

  // 🎯 Desbloquear ubicación
  async unlockLocation(id: number): Promise<Location> {
    const response = await this.httpClient.put<Location>(`/locations/${id}/unlock`, {});
    return response;
  }

  // 🎯 Generar etiqueta (retorna URL de descarga)
  async printLabel(id: number): Promise<Blob> {
    // La API devuelve el PDF directamente. Solicitamos como blob.
    const blob = await this.httpClient.get<Blob>(
      `/locations/${id}/print-label`,
      undefined,
      { responseType: 'blob' as any },
      true,
    );
    return blob;
  }

  // 🎯 Ajustar stock
  async adjustStock(payload: {
    product_id: number;
    qty: number;
    location_id: number;
    description?: string;
    batch_id?: number;
  }): Promise<void> {
    await this.httpClient.post('/stock-event/adjust-stock', payload, true);
  }
}
